import { Box, Button, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import Table, { ColumnType } from "components/Table";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { ResType } from "types";
import Loader from "components/Loader";
import { exportTanIncomeTaxMycas, getIncomeTaxMycas, getMyCaFormTypes } from "api/services/automation/income-tax-tan";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { getAutFinancialYears } from "./constants";
import SearchContainer from "components/SearchContainer";
import moment from "moment";
import useTitle from "hooks/useTitle";
import CampaignIcon from "@mui/icons-material/Campaign";
import IncomeTaxMycasInfo from "views/atom-pro/Tan/incomtaxMycasInfo";
import { usePermissions } from "context/PermissionsProvider";
import AccessDenied from "views/atom-pro/Access/accessDenide";
import { Permissions } from "data/permissons";
import { getCurrentFormattedDateTime } from "utils";
import { snack } from "components/toast";
import { handleError } from "utils/handleError";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';


const TanIncomeTaxMycas = (state) => {
  useTitle("ATOM Pro | AR (CA)");
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [assessmentYear, setAssessmentYear] = useState<any>(null);
  const [clientCategory, setClientCategory] = useState<any>(null);
  const [sortValue, setSort] = useState<any>(null);
  const [filingValue, setFilingType] = useState<any>(null);
  const { permissions } = usePermissions();

  const [search, setSearch] = useState("");
  const [formType, setFormTypeData] = useState<any>(null);
  const [openInfo, setOpenInfo] = useState(false);

  const { data, isLoading }: ResType = useQuery(
    [
      "get-tan-my-cas",
      {
        limit: pageCount,
        offset: page * pageCount,
        search,
        sortValue,
        assessmentYear,
        clientCategory,
        filingValue,
      },
    ],
    getIncomeTaxMycas,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },
      // enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)
    }
  );

  const { data: formTypeData, isLoading: formTypeIsloading }: ResType = useQuery(
    ["my-ca-form-types"],
    getMyCaFormTypes,
    {
      onSuccess: (res) => {
        setFormTypeData(res?.data);
      },
      // enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)
    }
  );

  const onAssessmentYearChange = (e: any) => {
    setAssessmentYear(e.target.value);
    setPage(0);
  };

  const onClientCategoryChange = (e: any) => {
    setClientCategory(e.target.value);
    setPage(0);
  };

  const onSortChange = (e: any) => {
    setPage(0);
    setSort(e.target.value);
  };

  const onFilingTypeChange = (e: any) => {
    setPage(0);
    setFilingType(e.target.value);
  };

  const handleSearch = (e: any) => {
    setPage(0);
    setSearch(e);
  };

  const columns: Array<ColumnType> = [
    {
      key: "id",
      title: "AY",
      render: (row: any) => {
        return (
          <Box>
            <Typography>{row?.assessmentYear ? `${row?.assessmentYear}` : "-"}</Typography>
          </Box>
        );
      },
    },
    {
      key: "name",
      title: (
        <Box display="flex" alignItems="center">
          {/* <PersonOutlineOutlinedIcon /> */}
          Client Name (TAN)
        </Box>
      ),
      render: (v) => (
        <Typography>
          {v?.tanClientCredentials?.client?.displayName} (<span>{v?.tanClientCredentials?.tanNumber}</span>)
        </Typography>
      ),
    },

    {
      key: "name",
      title: (
        <Box display="flex" alignItems="center">
          {/* <PersonOutlineOutlinedIcon /> */}
           CA Name (Membership #)
        </Box>
      ),
      render: (v) => (
        <Typography>
          {v?.caName} (<span>{v?.caMembershipNum}</span>)
        </Typography>
      ),
    },

    {
      key: "filingType",
      title: "Filing Type",
      render: (row: any) => {
        return (
          <Typography>
            {row?.filingType === "O" ? "Original" : row?.filingType === "R" ? "Revised" : "-"}
          </Typography>
        );
      },
    },
    {
      key: "formTypeCd",
      title: "Form Type",
    },
   
    {
      key: "assignedDate",
      title: "Assigned Date",
      render: (row: any) => {
        return (
          <Typography>
            {row?.assignedDate ? moment(row?.assignedDate).format("MMM DD, YYYY") : "-"}
          </Typography>
        );
      },
    },
    {
      key: "transactionNo",
      title: "Transaction ID",
    },
    {
      key: "udinNumber",
      title: "UDIN",
      render: (row: any) => {
        return <Typography>{row?.udinNumber ? row?.udinNumber : "-"}</Typography>;
      },
    },
  ];

  const { mutate: mutate1 } = useMutation(exportTanIncomeTaxMycas, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      const formattedDateTime = getCurrentFormattedDateTime();
      link.href = file;
      link.download = ` ATOM Pro -Income Tax Authorised CA ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Authorised CA exported successfully");
    },
    onError: (err: any) => {
      snack.error(err);
      snack.error(handleError(err));
    },
  });
  const handleExport = () => {
    mutate1({
      ...state,
      search,
      sortValue,
      assessmentYear,
      clientCategory,
      filingValue,
    });
  };

  // if (!(permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS))) {
  //   return <AccessDenied />
  // }

  if (isLoading) return <Loader />;
  return (
    <>
     <Box m={1}>
      <Box display="flex" m={1}>
        <Box ml={1}>
          <TextField
            name="assessmentYear"
            value={assessmentYear}
            onChange={onAssessmentYearChange}
            size="small"
            label="AY"
            select
            sx={{ width: 100 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            <MenuItem key={""} value={"null"}>
              NA
            </MenuItem>
            {getAutFinancialYears().map((year: string) => (
              <MenuItem key={year} value={year.split("-")[0]}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box ml={1}>
          <TextField
            name="clientCategory"
            value={clientCategory}
            onChange={onClientCategoryChange}
            size="small"
            label="Form Type"
            select
            sx={{ width: 110 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            {formType?.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box ml={1}>
          <TextField
            name="sortValue"
            value={sortValue}
            onChange={onSortChange}
            size="small"
            label="Sort By"
            select
            sx={{ width: 100 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            {[
              { value: "DATE_NEWEST", label: "Date Newest" },
              { value: "DATE_OLDEST", label: "Date Oldest" },
              { value: "UDIN_COMPLETED", label: "UDIN Completed" },
              { value: "UDIN_PENDING", label: "UDIN Pending" },
            ].map((sort) => (
              <MenuItem key={sort.value} value={sort.value}>
                {sort.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box ml={1}>
          <TextField
            name="filingType"
            value={filingValue}
            onChange={onFilingTypeChange}
            size="small"
            label="Filing Type"
            select
            sx={{ width: 110 }}
          >
            <MenuItem key={""} value={""}>
              Select
            </MenuItem>
            {[
              { value: "NA", label: "NA" },
              { value: "O", label: "Original" },
              { value: "R", label: "Revised" },
            ].map((sort) => (
              <MenuItem key={sort.value} value={sort.value}>
                {sort.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box ml={1}>
          <SearchContainer
            minWidth="450px"
            debounced
            value={search}
            placeHolder="Search by Client | TAN | CA Name | Membership #"
            onChange={handleSearch}
          />
        </Box>
        <Box ml={1}>
          <Button
            onClick={() => {
              setAssessmentYear(null);
              setClientCategory(null);
              setSort(null);
              setSearch("");
              setFilingType(null);
            }}
            size="medium"
            color="secondary"
            variant="outlined"
            // sx={{ width: 120 }}
          >
            Clear Filters
          </Button>
        </Box>
        <Button onClick={() => setOpenInfo(true)} variant="outlined" sx={{ mr: 1, ml: 3 }}>
          <CampaignIcon />
        </Button>
        <Button
                variant="outlined"
                color="secondary"
                onClick={handleExport}
                startIcon={<FileDownloadOutlinedIcon />}
            >
                Export
            </Button>
        <IncomeTaxMycasInfo open={openInfo} setOpen={setOpenInfo} />
      </Box>
      <Box>
        {data && data?.data.count > 0 ? (
          <>
            <Table
            
sx={{ pb: 0, mb: 0, height: '530px' }}
              pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
              data={data?.data?.result || []}
              columns={columns}
              loading={isLoading}
            />
          </>
        ) : (
          <>
            <Box textAlign="center" mt={20}>
              <Typography variant="subtitle1" color="rgba(0,0,0,0.5)">
                No Data present
              </Typography>
            </Box>
          </>
        )}
      </Box>
      </Box>
    </>
  );
};

export default TanIncomeTaxMycas;
